import { NAME_LIMITS, roomCodeSchema } from '@kokun/shared';
import { api } from '../../app/api';
import { ui } from '../../app/game-store';
import { connection } from '../../multiplayer/connection';
import { audio } from '../../game/audio/audio';
import { button, h, modal, toast } from '../dom';
import { openHowToPlay } from './how-to-play';
import { openSettings } from './settings-panel';
import { openAccount } from './account';

export interface WelcomeActions {
  quickplay(): Promise<void>;
  createRoom(visibility: 'public' | 'private'): Promise<void>;
  joinRoom(code: string): Promise<void>;
  practice(): void;
  openCreator(): void;
  ensureProfile(): Promise<boolean>;
  logout(): void;
  engineInfo(): { renderer: string; maxTexture: number };
}

/** Pantalla de bienvenida (la escena 3D animada queda de fondo). */
export function renderWelcome(a: WelcomeActions): HTMLElement {
  const status = h('div', { class: 'conn-pill' }, '● Comprobando servidor…');
  const refreshStatus = async () => {
    const ok = await api.health().catch(() => false);
    const s = connection.state.get().status;
    status.className = `conn-pill ${ok ? 'ok' : 'bad'}`;
    status.textContent = ok ? `● Servidor en línea${s === 'online' ? ` · ${connection.state.get().rtt} ms` : ''}` : '● Servidor no disponible (puedes jugar en modo práctica)';
  };
  void refreshStatus();
  const timer = setInterval(refreshStatus, 8000);

  const guard = (fn: () => Promise<void>) => async () => {
    audio.unlock();
    audio.play('click');
    if (!(await a.ensureProfile())) return;
    try {
      await fn();
    } catch (e) {
      audio.play('error');
      toast((e as Error).message, 'error');
    }
  };

  const joinDialog = () => {
    const input = h('input', { class: 'code-input', maxLength: 5, placeholder: 'ABCDE', autocomplete: 'off', spellcheck: false }) as HTMLInputElement;
    modal('Unirse a una partida', [h('p', null, 'Escribe el código de invitación que te ha pasado tu amigo:'), input], [
      { label: 'Cancelar', cls: 'btn ghost', run: () => undefined },
      {
        label: 'Entrar',
        cls: 'btn primary',
        run: async () => {
          const code = roomCodeSchema.safeParse(input.value);
          if (!code.success) {
            toast('El código tiene 5 letras/números.', 'error');
            return true;
          }
          await guard(() => a.joinRoom(code.data))();
        },
      },
    ]);
    setTimeout(() => input.focus(), 50);
  };

  const createDialog = () =>
    modal('Crear partida', h('p', null, '¿Quién puede entrar?'), [
      { label: 'Pública (partida rápida)', cls: 'btn', run: () => void guard(() => a.createRoom('public'))() },
      { label: 'Privada (solo con código)', cls: 'btn primary', run: () => void guard(() => a.createRoom('private'))() },
    ]);

  const profile = ui.get().profile;
  const el = h(
    'div',
    { class: 'welcome screen' },
    h('div', { class: 'logo-wrap' }, h('div', { class: 'logo' }, h('span', { class: 'l1' }, 'EL'), h('span', { class: 'l2' }, 'KOKUN'), h('span', { class: 'l3' }, 'GAME')), h('p', { class: 'tagline' }, 'Entra con tus amigos, completa la misión y no confíes demasiado en nadie.')),
    h(
      'nav',
      { class: 'menu' },
      button('▶  Jugar ahora', guard(a.quickplay), 'btn primary big', { 'data-testid': 'play' }),
      button('🎨  Crear personaje', guard(async () => a.openCreator()), 'btn big'),
      button('🔑  Unirse a una partida', () => (audio.unlock(), joinDialog()), 'btn big'),
      button('➕  Crear partida', () => (audio.unlock(), createDialog()), 'btn big'),
      h(
        'div',
        { class: 'menu-row' },
        button('❓ Cómo jugar', () => (audio.unlock(), openHowToPlay()), 'btn ghost'),
        button('⚙️ Ajustes', () => (audio.unlock(), openSettings(a.engineInfo())), 'btn ghost'),
        button('🏃 Práctica', () => (audio.unlock(), a.practice()), 'btn ghost'),
      ),
    ),
    h(
      'footer',
      { class: 'welcome-foot' },
      status,
      profile
        ? button(`👤 ${profile.player.name} · Nv ${profile.player.level}`, () => void openAccount(a.logout), 'btn ghost small', { 'data-testid': 'account' })
        : h('span', { class: 'muted small' }, 'Sin registro: elige un nombre y a jugar.'),
    ),
  );
  (el as HTMLElement & { cleanup?: () => void }).cleanup = () => clearInterval(timer);
  return el;
}

/** Pide un nombre de invitado (o iniciar sesión con una cuenta). */
export function askName(): Promise<boolean> {
  return new Promise((resolve) => {
    const name = h('input', { class: 'name-input', maxLength: NAME_LIMITS.MAX, placeholder: 'Tu nombre', autocomplete: 'nickname', 'data-testid': 'name' }) as HTMLInputElement;
    const user = h('input', { placeholder: 'usuario', autocomplete: 'username' }) as HTMLInputElement;
    const pass = h('input', { placeholder: 'contraseña', type: 'password', autocomplete: 'current-password' }) as HTMLInputElement;
    let done = false;
    const finish = (v: boolean) => {
      if (!done) {
        done = true;
        resolve(v);
      }
    };
    modal(
      '¿Cómo te llamas?',
      [
        h('p', null, 'Elige un nombre para que tus amigos te reconozcan. Sin correo, sin contraseñas.'),
        name,
        h('details', { class: 'login' }, h('summary', null, '¿Ya tienes cuenta? Inicia sesión'), h('div', { class: 'row' }, user, pass), button('Entrar con mi cuenta', async () => {
          try {
            const p = await api.login(user.value, pass.value);
            ui.set({ profile: p });
            document.querySelector('.modal-backdrop')?.remove();
            finish(true);
          } catch (e) {
            toast((e as Error).message, 'error');
          }
        }, 'btn small')),
      ],
      [
        { label: 'Cancelar', cls: 'btn ghost', run: () => finish(false) },
        {
          label: '¡A jugar!',
          cls: 'btn primary',
          run: async () => {
            try {
              const p = await api.guest(name.value);
              ui.set({ profile: p });
              finish(true);
            } catch (e) {
              toast((e as Error).message, 'error');
              return true;
            }
          },
        },
      ],
    );
    setTimeout(() => name.focus(), 50);
    name.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') (document.querySelector('.modal-actions .primary') as HTMLButtonElement | null)?.click();
    });
  });
}
